/**
 * @tms-phoenix/core/browser
 * Browser-specific core utilities
 *
 * Import this module in frontend applications:
 * import { createLogger, getTelemetry } from '@tms-phoenix/core/browser';
 */

// Re-export shared types
export type { Logger, LoggerFactory, LoggerConfig, LogContext, LogLevel } from '../logging/types.js';
export type {
  Telemetry,
  TelemetryConfig,
  Transaction,
  TransactionContext,
  MetricAttributes,
} from '../telemetry/types.js';

// Browser-specific implementations
export { BrowserLoggerFactory, type BrowserLoggerConfig } from './logging.js';
export { initializeBrowserTelemetry, NoopBrowserTelemetry, type BrowserTelemetryConfig } from './telemetry.js';

import { BrowserLoggerFactory, type BrowserLoggerConfig } from './logging.js';
import { initializeBrowserTelemetry, NoopBrowserTelemetry, type BrowserTelemetryConfig } from './telemetry.js';
import type { Logger, LoggerFactory, LogLevel } from '../logging/types.js';
import type { Telemetry } from '../telemetry/types.js';

let globalFactory: LoggerFactory | null = null;
let globalTelemetry: Telemetry | null = null;

/**
 * Initialize browser logging
 * Call once at application startup
 */
export function initializeLogging(config?: Partial<BrowserLoggerConfig>): LoggerFactory {
  globalFactory = new BrowserLoggerFactory(config);
  return globalFactory;
}

/**
 * Get or create the global logger factory
 */
export function getLoggerFactory(): LoggerFactory {
  if (!globalFactory) {
    globalFactory = new BrowserLoggerFactory();
  }
  return globalFactory;
}

/**
 * Create a logger with the given name
 */
export function createLogger(name: string): Logger {
  return getLoggerFactory().create(name);
}

/**
 * Set the global log level
 */
export function setLogLevel(level: LogLevel): void {
  getLoggerFactory().setLevel(level);
}

/**
 * Initialize browser telemetry
 * Call once at application startup
 */
export function initializeTelemetry(config: BrowserTelemetryConfig): Telemetry {
  globalTelemetry = initializeBrowserTelemetry(config);
  return globalTelemetry;
}

/**
 * Get the global telemetry instance
 */
export function getTelemetry(): Telemetry {
  if (!globalTelemetry) {
    globalTelemetry = new NoopBrowserTelemetry();
  }
  return globalTelemetry;
}

/**
 * Shutdown telemetry
 */
export async function shutdownTelemetry(): Promise<void> {
  if (globalTelemetry) {
    await globalTelemetry.shutdown();
    globalTelemetry = null;
  }
}
